import { ref, shallowRef, watch } from "vue";
import { useI18n } from "vue-i18n";
import { storeToRefs } from "pinia";
import BigNumber from "bignumber.js";
import { Decimal } from "@/libs/decimal";
import {
  BondingCurveTokenSale,
  initAffiliationTokenGatingTokenSale,
  toTokenDecimals,
} from "bctsl-sdk";
import {
  AeSdkBase,
  Contract,
  ContractMethodsBase,
  Encoded,
  toAe,
} from "@aeternity/aepp-sdk";
import { useQueryClient } from "@tanstack/vue-query";

import type { ITransaction } from "@/utils/types";
import { TokenDto } from "@/api/generated";
import { TRANSACTION_STATES, TRANSACTION_TYPES } from "@/utils/constants";
import { fetchJson } from "@/utils";
import {
  useAccounts,
  useTokenTradeStore,
  useTransactionConfirmModal,
} from "@/stores";

import { useAeppSdk } from "./aeppSdk";
import { useNetwork } from "./useNetwork";
import {
  calculateBuyPrice,
  calculateBuyPriceWithAffiliationFee,
  calculateSellReturn,
  calculateTokensFromAE,
  toDecimals,
} from "@/utils/calculators";

export interface UseTokenTradeProps {
  token: TokenDto;
}

const tokenSaleInstance = shallowRef<BondingCurveTokenSale>();
const bondingCurveInstance = shallowRef<Contract<ContractMethodsBase>>();

export function useTokenTrade({ token }: UseTokenTradeProps) {
  const {
    tokenA,
    tokenB,
    tokenAFocused,
    token: tokenStore,
    isBuying,
    isAllowSelling,
    loadingTransaction,
    nextPrice,
    userBalance,
    desiredSlippage,
    successTxData,
  } = storeToRefs(useTokenTradeStore());
  const transactionConfirmModal = useTransactionConfirmModal();
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const { activeAccount, slippage } = storeToRefs(useAccounts());
  const { staticAeSdk, getAeSdk } = useAeppSdk();
  const { activeNetwork } = useNetwork();

  const errorMessage = ref();

  const currentState = ref<TRANSACTION_STATES>(
    staticAeSdk
      ? TRANSACTION_STATES.CONNECTED
      : TRANSACTION_STATES.NOT_CONNECTED
  );

  async function resetFormState() {
    currentState.value = TRANSACTION_STATES.CONNECTED;
    nextPrice.value = Decimal.ZERO;
    successTxData.value = undefined;
  }

  async function buy() {
    errorMessage.value = undefined;
    if (!tokenSaleInstance.value) {
      errorMessage.value = t("blocks.tokenTradeCard.contractNotInitialized");
      return;
    }

    if (!tokenB.value) {
      errorMessage.value = t("blocks.tokenTradeCard.amountNotSet");
      return;
    }

    loadingTransaction.value = true;
    try {
      const buyResult = (await tokenSaleInstance.value.buy(
        tokenB.value,
        undefined,
        slippage.value
      )) as ITransaction | any;
      const mints: any[] = buyResult.decodedEvents.filter(
        (data: any) => data.name === "Mint"
      );
      const _userBalance = await onTransactionComplete();
      const protocolSymbol = await getTokenSymbolName(
        mints[0].contract.address
      );
      successTxData.value = {
        isBuying: true,
        destAmount: Decimal.from(toAe(mints[mints.length - 1].args[1])),
        sourceAmount: Decimal.from(
          toAe(
            buyResult.decodedEvents.find((data: any) => data.name === "Buy")
              .args[0]
          )
        ),
        symbol: token.symbol,
        protocolReward: Decimal.from(toAe(mints[0].args[1])),
        protocolSymbol,
        userBalance: Decimal.from(_userBalance),
      };
      currentState.value = TRANSACTION_STATES.SUCCESS;
    } catch (error: any) {
      errorMessage.value = error?.message;
    }
    loadingTransaction.value = false;
  }

  async function sell() {
    errorMessage.value = null;
    if (!tokenSaleInstance.value) {
      errorMessage.value = t("blocks.tokenTradeCard.contractNotInitialized");
      return;
    }

    if (!tokenA.value) {
      errorMessage.value = t("blocks.tokenTradeCard.amountNotSet");
      return;
    }

    loadingTransaction.value = true;
    try {
      isAllowSelling.value = true;

      const countTokenDecimals =
        await tokenSaleInstance.value.createSellAllowance(
          tokenA.value?.toString()
        );

      isAllowSelling.value = false;

      const sellResult =
        (await tokenSaleInstance.value.sellWithExistingAllowance(
          countTokenDecimals,
          slippage.value
        )) as ITransaction | any;
      const _userBalance = await onTransactionComplete();
      successTxData.value = {
        isBuying: false,
        destAmount: Decimal.from(
          toAe(
            sellResult.decodedEvents.find((data: any) => data.name === "Burn")
              .args[1]
          )
        ),
        sourceAmount: Decimal.from(
          toAe(
            sellResult.decodedEvents.find((data: any) => data.name === "Sell")
              .args[0]
          )
        ),
        symbol: token.symbol,
        userBalance: Decimal.from(_userBalance),
      };

      await onTransactionComplete();

      currentState.value = TRANSACTION_STATES.SUCCESS;
    } catch (error: any) {
      errorMessage.value = error?.message;
    }
    loadingTransaction.value = false;
  }

  async function onTransactionComplete() {
    queryClient.invalidateQueries({
      queryKey: ["TokensService.findByAddress", token.sale_address],
    });
    queryClient.invalidateQueries({
      queryKey: ["MiddlewareService.getTxsByScope", token.sale_address],
    });
    return fetchUserTokenBalance();
  }

  async function fetchUserTokenBalance() {
    if (!tokenSaleInstance.value) {
      throw new Error(t("blocks.tokenTradeCard.contractNotInitialized"));
    }
    let balance = "0";

    const tokenContract = await tokenSaleInstance.value.tokenContractInstance();
    if (activeAccount.value) {
      const result: { decodedResult?: bigint } = await tokenContract.balance(
        activeAccount.value
      );

      if (!result) throw new Error("User balance not fetched");
      const tokenBalance = result.decodedResult || 0n;

      if (!token) throw new Error("metaInfo not fetched");

      balance = toTokenDecimals(
        tokenBalance,
        (token.decimals || 18n) as any,
        0n
      );
    }
    userBalance.value = balance.toString();

    return balance;
  }

  async function getTokenSymbolName(tokenAddress: Encoded.ContractAddress) {
    const data = await fetchJson(
      `${activeNetwork.value.middlewareUrl}/v3/aex9/${tokenAddress}`
    );
    return data?.symbol || data?.name || "BCL";
  }

  async function setupContractInstance(sdk: AeSdkBase) {
    tokenSaleInstance.value = await initAffiliationTokenGatingTokenSale(
      sdk,
      token.sale_address as Encoded.ContractAddress
    );

    bondingCurveInstance.value = (
      await tokenSaleInstance.value.bondingCurveContract()
    ).instance;

    if (activeAccount.value) {
      await fetchUserTokenBalance();
    }
  }

  async function onConfirm() {
    transactionConfirmModal.setTransactionType(TRANSACTION_TYPES.TRADE);
    desiredSlippage.value = slippage.value;
    tokenStore.value = token;
    if (isBuying.value) {
      buy().finally(() => {
        transactionConfirmModal.clearData();
      });
    } else {
      sell().finally(() => {
        transactionConfirmModal.clearData();
      });
    }
  }

  // Retrieve the next price using bonding curve instance
  function calculateNextPrice(currentSupply: Decimal) {
    try {
      nextPrice.value = Decimal.from(
        toAe(
          calculateBuyPrice(
            new BigNumber(currentSupply.bigNumber),
            new BigNumber(1).multipliedBy(new BigNumber(10).pow(18))
          )
        )
      );
    } catch (error) {
      console.error("Error calculating bonding curve price:", error);
      nextPrice.value = Decimal.from(0);
    }
  }

  function calculateTokenCost(
    amount?: number,
    _isBuying = false,
    _isUsingToken = false
  ) {
    const tokenDecimals = token.decimals ?? 18;
    const tokenSupply = new BigNumber(token.total_supply ?? 0);
    let currentSupply = Decimal.from(toAe(tokenSupply.toString()));

    if (!amount || amount <= 0) {
      calculateNextPrice(currentSupply);
      return amount;
    }

    const tokenAmountBigNumber = new BigNumber(
      toDecimals(amount, tokenDecimals).toString()
    );
    let tokenAmountCostDecimal: Decimal;

    if (_isBuying) {
      if (_isUsingToken) {
        currentSupply = currentSupply.add(Decimal.from(amount));
        tokenAmountCostDecimal = Decimal.from(
          toAe(
            calculateBuyPriceWithAffiliationFee(
              tokenSupply,
              tokenAmountBigNumber
            )
          )
        );
      } else {
        tokenAmountCostDecimal = Decimal.from(
          calculateTokensFromAE(tokenSupply, amount).toFixed(2)
        );
        currentSupply = currentSupply.add(tokenAmountCostDecimal.toString());
      }
    } else {
      if (_isUsingToken) {
        currentSupply = currentSupply.sub(Decimal.from(amount));
        tokenAmountCostDecimal = Decimal.from(
          toAe(calculateSellReturn(tokenSupply, tokenAmountBigNumber))
        );
      } else {
        tokenAmountCostDecimal = Decimal.from(
          calculateTokensFromAE(tokenSupply, amount).toFixed(2)
        );
        currentSupply = currentSupply.sub(tokenAmountCostDecimal.toString());
      }
    }

    calculateNextPrice(currentSupply);

    return parseFloat(tokenAmountCostDecimal.toString());
  }

  watch(
    isBuying,
    () => {
      if (currentState.value === TRANSACTION_STATES.SUCCESS) {
        resetFormState();
      }
    },
    {
      immediate: true,
    }
  );

  watch(
    () => token,
    async (_token, oldToken) => {
      if (!_token) return;
      if (_token.address !== oldToken?.address) {
        resetFormState();
      }
      tokenStore.value = _token;
    },
    {
      immediate: true,
    }
  );

  /**
   * Watch TokenA
   */
  watch(
    [tokenA, isBuying, tokenAFocused],
    async (
      [newTokenA, isBuyingToken, newTokenAFocused],
      [oldTokenA, oldIsBuyingToken]
    ) => {
      if (
        // !newTokenA ||
        // isNaN(Number(newTokenA)) ||
        !tokenSaleInstance.value ||
        !newTokenAFocused ||
        (newTokenA === oldTokenA && isBuyingToken === oldIsBuyingToken)
      ) {
        return;
      }
      tokenB.value = calculateTokenCost(
        newTokenA,
        isBuyingToken,
        !isBuyingToken
      );
    }
  );

  /**
   * Watch TokenB
   */
  watch(
    [tokenB, isBuying, tokenAFocused],
    async (
      [newTokenB, isBuyingToken, newTokenAFocused],
      [oldTokenB, oldIsBuyingToken]
    ) => {
      if (
        // !newTokenB ||
        // isNaN(Number(newTokenB)) ||
        !tokenSaleInstance.value ||
        newTokenAFocused ||
        (newTokenB === oldTokenB && isBuyingToken === oldIsBuyingToken)
      ) {
        return;
      }
      tokenA.value = calculateTokenCost(
        newTokenB,
        isBuyingToken,
        isBuyingToken
      );
    }
  );

  watch(
    activeAccount,
    async () => {
      if (activeAccount.value) {
        currentState.value = TRANSACTION_STATES.CONNECTED;
        const sdk = await getAeSdk();
        await setupContractInstance(sdk);
      } else {
        currentState.value = TRANSACTION_STATES.NOT_CONNECTED;
        await setupContractInstance(staticAeSdk);
      }
      // void fetchTotalSupply();
    },
    {
      immediate: true,
    }
  );

  return {
    // methods
    resetFormState,
    onConfirm,
    // computed
    currentState,
    errorMessage,
  };
}
